import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DollarSign, BarChart3, RefreshCw, Plus, Trash2,
  CheckCircle2, XCircle, Clock, TrendingUp, TrendingDown,
  Award, Target, History, Edit2,
} from "lucide-react";
import { apiClient } from "../services/api";
import { toast } from "sonner";
import i18n from "../i18n";

const TABS = [
  { id: "dashboard", icon: BarChart3 },
  { id: "bands",     icon: Target },
  { id: "cycles",    icon: RefreshCw },
  { id: "reviews",   icon: Award },
  { id: "history",   icon: History },
];

const STATUS_STYLE = {
  pending:  "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  approved: "bg-green-500/15 text-green-400 border-green-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  draft:    "bg-gray-500/15 text-gray-400 border-gray-500/30",
  active:   "bg-blue-500/15 text-blue-400 border-blue-500/30",
  closed:   "bg-gray-500/15 text-gray-400 border-gray-500/30",
};

const statusLabel = (status, t) => t(`compensationHub.status.${status}`, { defaultValue: status });
const changeTypeLabel = (type, t) => t(`compensationHub.changeType.${type}`, { defaultValue: type });

const fmt = (n, currency = "TND") =>
  n == null ? "—" : `${Number(n).toLocaleString(i18n.language)} ${currency}`;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(i18n.language) : "—");

// ── Dashboard Tab ─────────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("v1/compensation/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center py-20 text-[var(--lm-muted)]">{t("compensationHub.loading")}</div>;

  const kpis = [
    { label: t("compensationHub.dashboard.kpiActiveCycles"), value: data.active_cycles, icon: RefreshCw, color: "blue" },
    { label: t("compensationHub.dashboard.kpiBands"), value: data.total_bands, icon: Target, color: "purple" },
    { label: t("compensationHub.dashboard.kpiPendingReviews"), value: data.pending_reviews, icon: Clock, color: "yellow" },
    { label: t("compensationHub.dashboard.kpiAvgIncrease"), value: data.avg_increase_pct != null ? `${data.avg_increase_pct}%` : "—", icon: TrendingUp, color: "green" },
  ];
  const colorBg  = { blue: "bg-blue-500/10",   purple: "bg-purple-500/10", yellow: "bg-yellow-500/10", green: "bg-green-500/10" };
  const colorText= { blue: "text-blue-400",    purple: "text-purple-400",  yellow: "text-yellow-400",  green: "text-green-400" };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => (
          <div key={k.label} className={`p-4 rounded-xl border border-[var(--lm-border)] ${colorBg[k.color]}`}>
            <k.icon className={`w-5 h-5 mb-2 ${colorText[k.color]}`} />
            <p className="text-2xl font-bold text-[var(--lm-text)]">{k.value ?? "0"}</p>
            <p className="text-xs text-[var(--lm-muted)] mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {data.total_budget > 0 && (
        <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[var(--lm-text)]">{t("compensationHub.dashboard.budgetTitle")}</h3>
            <span className="text-sm text-[var(--lm-muted)]">{t("compensationHub.dashboard.budgetUsed", { pct: data.budget_used_pct ?? 0 })}</span>
          </div>
          <div className="w-full h-3 bg-[var(--lm-bg)] rounded-full overflow-hidden">
            <div className="h-3 bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(data.budget_used_pct ?? 0, 100)}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-[var(--lm-muted)]">
            <span>{t("compensationHub.dashboard.committed", { amount: fmt(data.approved_cost, data.currency) })}</span>
            <span>{t("compensationHub.dashboard.totalBudget", { amount: fmt(data.total_budget, data.currency) })}</span>
          </div>
        </div>
      )}

      {data.recent_changes?.length > 0 && (
        <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
          <h3 className="font-semibold text-[var(--lm-text)] mb-4">{t("compensationHub.dashboard.recentChanges")}</h3>
          <div className="space-y-2">
            {data.recent_changes.map(h => (
              <div key={h.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--lm-bg)]">
                <div>
                  <p className="text-sm font-medium text-[var(--lm-text)]">{h.employee_name}</p>
                  <p className="text-xs text-[var(--lm-muted)]">{changeTypeLabel(h.change_type, t)} · {fmtDate(h.effective_date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-[var(--lm-text)]">{fmt(h.salary, h.currency)}</p>
                  {h.change_pct != null && (
                    <p className={`text-xs flex items-center gap-1 justify-end ${h.change_pct >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {h.change_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {h.change_pct >= 0 ? "+" : ""}{h.change_pct}%
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Salary Bands Tab ──────────────────────────────────────────────────────────

function BandModal({ band, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: band?.name || "",
    grade: band?.grade || "",
    job_family: band?.job_family || "",
    currency: band?.currency || "TND",
    min_salary: band?.min_salary || "",
    mid_salary: band?.mid_salary || "",
    max_salary: band?.max_salary || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.grade || !form.min_salary || !form.max_salary) {
      toast.error(t("compensationHub.bands.requiredFields")); return;
    }
    setLoading(true);
    try {
      const payload = { ...form, min_salary: parseFloat(form.min_salary), max_salary: parseFloat(form.max_salary) };
      payload.mid_salary = form.mid_salary ? parseFloat(form.mid_salary) : null;
      if (band) await apiClient.put(`v1/compensation/bands/${band.id}`, payload);
      else await apiClient.post("v1/compensation/bands", payload);
      toast.success(t("compensationHub.bands.saved"));
      onSaved();
    } catch { toast.error(t("compensationHub.common.error")); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl">
        <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
          <h2 className="font-semibold text-[var(--lm-text)]">{band ? t("compensationHub.bands.titleEdit") : t("compensationHub.bands.titleNew")}</h2>
          <button onClick={onClose} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.bands.fieldName")}</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t("compensationHub.bands.namePlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.bands.fieldGrade")}</label>
              <input value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))}
                placeholder={t("compensationHub.bands.gradePlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.bands.fieldJobFamily")}</label>
              <input value={form.job_family} onChange={e => setForm(f => ({ ...f, job_family: e.target.value }))}
                placeholder={t("compensationHub.bands.jobFamilyPlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.bands.fieldCurrency")}</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                {["TND", "EUR", "USD", "MAD", "QAR", "SAR"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[["min_salary", t("compensationHub.bands.fieldMin")], ["mid_salary", t("compensationHub.bands.fieldMid")], ["max_salary", t("compensationHub.bands.fieldMax")]].map(([field, label]) => (
              <div key={field}>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{label}</label>
                <input type="number" value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("compensationHub.common.cancel")}</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
              {loading ? t("compensationHub.bands.saving") : t("compensationHub.bands.saveCta")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BandsTab() {
  const { t } = useTranslation();
  const [bands, setBands] = useState([]);
  const [modal, setModal] = useState(null); // null | "new" | band object

  const load = useCallback(() => apiClient.get("v1/compensation/bands").then(r => setBands(Array.isArray(r.data) ? r.data : [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm(t("compensationHub.bands.confirmDelete"))) return;
    await apiClient.delete(`v1/compensation/bands/${id}`);
    toast.success(t("compensationHub.bands.deleted")); load();
  };

  const families = [...new Set(bands.map(b => b.job_family || t("compensationHub.bands.others")))];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--lm-muted)]">{t("compensationHub.bands.count", { count: bands.length })}</p>
        <button onClick={() => setModal("new")}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("compensationHub.bands.new")}
        </button>
      </div>

      {families.map(fam => (
        <div key={fam}>
          <p className="text-xs font-semibold text-[var(--lm-muted)] uppercase tracking-wider mb-2">{fam}</p>
          <div className="space-y-2">
            {bands.filter(b => (b.job_family || t("compensationHub.bands.others")) === fam).map(b => (
              <div key={b.id} className="p-4 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[var(--lm-text)] text-sm">{b.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">{b.grade}</span>
                    {!b.is_active && <span className="text-xs px-2 py-0.5 rounded bg-gray-500/10 text-gray-400">{t("compensationHub.bands.inactive")}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-[var(--lm-muted)]">{t("compensationHub.bands.min")} {fmt(b.min_salary, b.currency)}</span>
                    {b.mid_salary && <span className="text-xs text-blue-400">| {t("compensationHub.bands.med")} {fmt(b.mid_salary, b.currency)}</span>}
                    <span className="text-xs text-[var(--lm-muted)]">| {t("compensationHub.bands.max")} {fmt(b.max_salary, b.currency)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setModal(b)} className="p-1.5 rounded text-[var(--lm-muted)] hover:text-[var(--lm-text)] hover:bg-[var(--lm-bg)]">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(b.id)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {bands.length === 0 && (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <Target className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("compensationHub.bands.empty")}</p>
        </div>
      )}

      {modal && (
        <BandModal
          band={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Cycles Tab ────────────────────────────────────────────────────────────────

function CycleModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", cycle_type: "merit",
    start_date: "", end_date: "",
    budget_pool: "", budget_pct: "", notes: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date) { toast.error(t("compensationHub.cycleModal.requiredFields")); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      payload.budget_pool = form.budget_pool ? parseFloat(form.budget_pool) : null;
      payload.budget_pct = form.budget_pct ? parseFloat(form.budget_pct) : null;
      await apiClient.post("v1/compensation/cycles", payload);
      toast.success(t("compensationHub.cycleModal.created")); onSaved();
    } catch { toast.error(t("compensationHub.common.error")); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl">
        <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
          <h2 className="font-semibold text-[var(--lm-text)]">{t("compensationHub.cycleModal.title")}</h2>
          <button onClick={onClose} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.cycleModal.fieldName")}</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t("compensationHub.cycleModal.namePlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.cycleModal.fieldType")}</label>
            <select value={form.cycle_type} onChange={e => setForm(f => ({ ...f, cycle_type: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
              <option value="merit">{t("compensationHub.cycleModal.typeMerit")}</option>
              <option value="promotion">{t("compensationHub.cycleModal.typePromotion")}</option>
              <option value="equity">{t("compensationHub.cycleModal.typeEquity")}</option>
              <option value="market">{t("compensationHub.cycleModal.typeMarket")}</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.cycleModal.fieldStartDate")}</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.cycleModal.fieldEndDate")}</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.cycleModal.fieldBudgetPool")}</label>
              <input type="number" value={form.budget_pool} onChange={e => setForm(f => ({ ...f, budget_pool: e.target.value }))}
                placeholder="50000" className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.cycleModal.fieldBudgetPct")}</label>
              <input type="number" step="0.1" value={form.budget_pct} onChange={e => setForm(f => ({ ...f, budget_pct: e.target.value }))}
                placeholder="3.5" className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("compensationHub.common.cancel")}</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
              {loading ? t("compensationHub.cycleModal.creating") : t("compensationHub.cycleModal.createCta")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CyclesTab() {
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => apiClient.get("v1/compensation/cycles").then(r => setCycles(Array.isArray(r.data) ? r.data : [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    await apiClient.put(`v1/compensation/cycles/${id}`, { status });
    toast.success(t("compensationHub.cycles.statusChanged", { status: statusLabel(status, t).toLowerCase() })); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--lm-muted)]">{t("compensationHub.cycles.count", { count: cycles.length })}</p>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("compensationHub.cycles.new")}
        </button>
      </div>

      {cycles.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <RefreshCw className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("compensationHub.cycles.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {cycles.map(c => (
            <div key={c.id} className="p-4 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[var(--lm-text)]">{c.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[c.status]}`}>{statusLabel(c.status, t)}</span>
                    <span className="text-xs text-[var(--lm-muted)] capitalize">{changeTypeLabel(c.cycle_type, t)}</span>
                  </div>
                  <p className="text-xs text-[var(--lm-muted)] mt-1">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</p>
                  {c.budget_pool && <p className="text-xs text-blue-400 mt-1">{t("compensationHub.cycles.budget", { amount: fmt(c.budget_pool) })}{c.budget_pct ? ` (${c.budget_pct}%)` : ""}</p>}
                  <p className="text-xs text-[var(--lm-muted)] mt-1">
                    {t("compensationHub.cycles.reviewsLine", { reviews: c.review_count ?? 0, approved: c.approved_count ?? 0 })}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {c.status === "draft" && (
                    <button onClick={() => changeStatus(c.id, "active")}
                      className="text-xs px-3 py-1.5 rounded-lg bg-blue-600/10 text-blue-400 hover:bg-blue-600/20 border border-blue-500/20">
                      {t("compensationHub.cycles.activate")}
                    </button>
                  )}
                  {c.status === "active" && (
                    <button onClick={() => changeStatus(c.id, "closed")}
                      className="text-xs px-3 py-1.5 rounded-lg bg-gray-500/10 text-gray-400 hover:bg-gray-500/20 border border-gray-500/20">
                      {t("compensationHub.cycles.close")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <CycleModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

// ── Reviews Tab ───────────────────────────────────────────────────────────────

function ReviewsTab() {
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ employee_id: "", current_salary: "", proposed_salary: "", increase_type: "merit", effective_date: "" });

  useEffect(() => {
    apiClient.get("v1/compensation/cycles").then(r => { setCycles(r.data); if (r.data.length) setSelectedCycle(r.data[0].id); });
    apiClient.get("v1/employees/?limit=200").then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.employees || [])));
  }, []);

  useEffect(() => {
    if (!selectedCycle) return;
    apiClient.get(`v1/compensation/reviews?cycle_id=${selectedCycle}`).then(r => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [selectedCycle]);

  const addReview = async (e) => {
    e.preventDefault();
    if (!addForm.employee_id || !addForm.current_salary) { toast.error(t("compensationHub.reviews.requiredFields")); return; }
    const payload = {
      cycle_id: parseInt(selectedCycle),
      employee_id: parseInt(addForm.employee_id),
      current_salary: parseFloat(addForm.current_salary),
      proposed_salary: addForm.proposed_salary ? parseFloat(addForm.proposed_salary) : null,
      increase_type: addForm.increase_type,
      effective_date: addForm.effective_date || null,
    };
    try {
      await apiClient.post("v1/compensation/reviews", payload);
      toast.success(t("compensationHub.reviews.added"));
      setShowAddModal(false);
      apiClient.get(`v1/compensation/reviews?cycle_id=${selectedCycle}`).then(r => setReviews(Array.isArray(r.data) ? r.data : []));
    } catch { toast.error(t("compensationHub.common.error")); }
  };

  const approve = async (id) => {
    try {
      await apiClient.post(`v1/compensation/reviews/${id}/approve`, {});
      toast.success(t("compensationHub.reviews.approved"));
      apiClient.get(`v1/compensation/reviews?cycle_id=${selectedCycle}`).then(r => setReviews(Array.isArray(r.data) ? r.data : []));
    } catch { toast.error(t("compensationHub.common.error")); }
  };

  const reject = async (id) => {
    try {
      await apiClient.post(`v1/compensation/reviews/${id}/reject`, {});
      toast.error(t("compensationHub.reviews.rejected"));
      apiClient.get(`v1/compensation/reviews?cycle_id=${selectedCycle}`).then(r => setReviews(Array.isArray(r.data) ? r.data : []));
    } catch { toast.error(t("compensationHub.common.error")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <select value={selectedCycle} onChange={e => setSelectedCycle(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
          <option value="">{t("compensationHub.reviews.selectCycle")}</option>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {selectedCycle && (
          <button onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
            <Plus className="w-4 h-4" /> {t("compensationHub.reviews.add")}
          </button>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <Award className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("compensationHub.reviews.empty")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--lm-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--lm-s1)] text-left">
                {[
                  t("compensationHub.reviews.colEmployee"),
                  t("compensationHub.reviews.colCurrentSalary"),
                  t("compensationHub.reviews.colProposedSalary"),
                  t("compensationHub.reviews.colIncrease"),
                  t("compensationHub.reviews.colType"),
                  t("compensationHub.reviews.colStatus"),
                  t("compensationHub.reviews.colActions"),
                ].map(h => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-[var(--lm-muted)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--lm-border)]">
              {reviews.map(r => (
                <tr key={r.id} className="bg-[var(--lm-bg)] hover:bg-[var(--lm-s1)]">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--lm-text)]">{r.employee_name}</p>
                    <p className="text-xs text-[var(--lm-muted)]">{r.employee_number}</p>
                  </td>
                  <td className="px-4 py-3 text-[var(--lm-text)]">{fmt(r.current_salary, r.currency)}</td>
                  <td className="px-4 py-3 text-[var(--lm-text)]">{r.proposed_salary ? fmt(r.proposed_salary, r.currency) : "—"}</td>
                  <td className="px-4 py-3">
                    {r.increase_pct != null ? (
                      <span className={`flex items-center gap-1 font-medium ${r.increase_pct >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {r.increase_pct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        {r.increase_pct >= 0 ? "+" : ""}{r.increase_pct}%
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--lm-muted)] capitalize">{changeTypeLabel(r.increase_type, t)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[r.status]}`}>{statusLabel(r.status, t)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {r.status === "pending" && (
                      <div className="flex gap-2">
                        <button onClick={() => approve(r.id)} className="p-1 rounded text-green-400 hover:bg-green-500/10"><CheckCircle2 className="w-4 h-4" /></button>
                        <button onClick={() => reject(r.id)} className="p-1 rounded text-red-400 hover:bg-red-500/10"><XCircle className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
              <h2 className="font-semibold text-[var(--lm-text)]">{t("compensationHub.reviews.addModalTitle")}</h2>
              <button onClick={() => setShowAddModal(false)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
            </div>
            <form onSubmit={addReview} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.reviews.fieldEmployee")}</label>
                <select value={addForm.employee_id}
                  onChange={e => {
                    const emp = employees.find(x => String(x.id) === e.target.value);
                    setAddForm(f => ({ ...f, employee_id: e.target.value, current_salary: emp?.salary ?? f.current_salary }));
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                  <option value="">{t("compensationHub.reviews.selectPlaceholder")}</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.reviews.fieldCurrentSalary")}</label>
                  <input type="number" value={addForm.current_salary} onChange={e => setAddForm(f => ({ ...f, current_salary: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.reviews.fieldProposedSalary")}</label>
                  <input type="number" value={addForm.proposed_salary} onChange={e => setAddForm(f => ({ ...f, proposed_salary: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.reviews.fieldIncreaseType")}</label>
                  <select value={addForm.increase_type} onChange={e => setAddForm(f => ({ ...f, increase_type: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                    {["hire", "merit", "promotion", "market", "equity", "correction"].map(v => <option key={v} value={v}>{changeTypeLabel(v, t)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.reviews.fieldEffectiveDate")}</label>
                  <input type="date" value={addForm.effective_date} onChange={e => setAddForm(f => ({ ...f, effective_date: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("compensationHub.common.cancel")}</button>
                <button type="submit" className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">{t("compensationHub.reviews.addCta")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── History Tab ───────────────────────────────────────────────────────────────

function HistoryTab() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [history, setHistory] = useState([]);

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.employees || [])));
  }, []);

  useEffect(() => {
    if (!selectedEmp) return;
    apiClient.get(`v1/compensation/history?employee_id=${selectedEmp}`).then(r => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [selectedEmp]);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("compensationHub.history.fieldEmployee")}</label>
        <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}
          className="w-64 px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
          <option value="">{t("compensationHub.history.selectPlaceholder")}</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
        </select>
      </div>

      {selectedEmp && history.length === 0 && (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <History className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("compensationHub.history.empty")}</p>
        </div>
      )}

      {history.length > 0 && (
        <div className="relative pl-6">
          <div className="absolute left-2 top-0 bottom-0 w-px bg-[var(--lm-border)]" />
          {history.map((h, i) => (
            <div key={h.id} className="relative mb-4 pl-4">
              <div className="absolute -left-4 top-2 w-3 h-3 rounded-full bg-blue-500 border-2 border-[var(--lm-bg)]" />
              <div className="p-4 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs text-[var(--lm-muted)]">{fmtDate(h.effective_date)}</p>
                    <p className="font-semibold text-[var(--lm-text)] text-lg">{fmt(h.salary, h.currency)}</p>
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      {changeTypeLabel(h.change_type, t)}
                    </span>
                  </div>
                  {h.change_pct != null && (
                    <div className={`text-right ${h.change_pct >= 0 ? "text-green-400" : "text-red-400"}`}>
                      <p className="font-bold">{h.change_pct >= 0 ? "+" : ""}{h.change_pct}%</p>
                      {h.change_amount != null && (
                        <p className="text-xs">{h.change_amount >= 0 ? "+" : ""}{fmt(h.change_amount, h.currency)}</p>
                      )}
                    </div>
                  )}
                </div>
                {h.notes && <p className="text-xs text-[var(--lm-muted)] mt-2">{h.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Hub ───────────────────────────────────────────────────────────────────────

export default function CompensationHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderTab = () => {
    switch (activeTab) {
      case "dashboard": return <DashboardTab />;
      case "bands":     return <BandsTab />;
      case "cycles":    return <CyclesTab />;
      case "reviews":   return <ReviewsTab />;
      case "history":   return <HistoryTab />;
      default:          return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-green-500/10 border border-green-500/20">
          <DollarSign className="w-6 h-6 text-green-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--lm-text)]">{t("compensationHub.title")}</h1>
          <p className="text-sm text-[var(--lm-muted)]">{t("compensationHub.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--lm-border)] overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? "border-green-500 text-green-400"
                : "border-transparent text-[var(--lm-muted)] hover:text-[var(--lm-text)]"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {t(`compensationHub.tabs.${tab.id}`)}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  );
}
